import { useMemo } from 'react'

import { Icon } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import { MaybeNull, TransactionStatusChip } from '@shared/components/common'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import type { Locale } from '@circlefin/money/variants'
import type { Payment } from '@shared/graphql'

const textClassNames = 'text-sm font-circular-book leading-5 text-black-600'

const columnHelper = createColumnHelper<Payment>()

export const useAddressTransactionsTableColumns = () => {
  const { t } = useTranslation('address-book/containers')
  const { locale } = useRouter()
  const { money } = useMoney({ locale: locale as Locale })
  const { date: formatDate } = useDate({ locale: locale as Locale })

  return useMemo(() => {
    /**
     * Transaction Type column.
     */
    const transactionType = columnHelper.accessor('type', {
      id: 'transactionType',
      header: t<string>`transactions-table.columns.transactionType`,
      meta: {
        className: 'w-1/5',
      },
      cell: ({ getValue }) => (
        <span className={classNames(textClassNames, 'font-circular-book')}>
          <Icon
            className="-mt-1 mr-4 inline text-black-600"
            name="LinkOutline"
            size={15}
          />
          {t(`transactions-table.transactionTypeLabels.${getValue()}`)}
        </span>
      ),
    })

    /**
     * Transaction Amount Column.
     */
    const amount = columnHelper.accessor('amount', {
      header: t<string>`transactions-table.columns.amount`,
      meta: {
        className: 'w-1/5',
      },
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <span className={classNames(textClassNames, 'font-circular-book')}>
            <MaybeNull>
              {money({
                number: value.amount,
                variant: value.currency,
                options: {
                  symbol: false,
                  signDisplay: false,
                },
              })}
            </MaybeNull>
          </span>
        )
      },
    })

    /**
     * Transaction Date Column.
     */
    const date = columnHelper.accessor('createDate', {
      header: t<string>`transactions-table.columns.date`,
      meta: {
        className: 'w-1/5',
      },
      cell: ({ getValue }) => (
        <span className={textClassNames}>{formatDate(getValue(), 'date')}</span>
      ),
    })

    /**
     * Transaction Type Column.
     */
    const type = columnHelper.accessor('type', {
      header: t<string>`transactions-table.columns.type`,
      meta: {
        className: 'w-1/5',
      },
      cell: () => (
        <span className={textClassNames}>{t`transactions-table.type`}</span>
      ),
    })

    /**
     * Transaction Status Column.
     */
    const status = columnHelper.accessor('status', {
      header: t<string>`transactions-table.columns.status`,
      meta: {
        className: 'w-1/5',
      },
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <div className="flex items-center justify-between">
            <TransactionStatusChip status={value} />
          </div>
        )
      },
    })

    return [transactionType, amount, date, type, status]
  }, [formatDate, money, t])
}
